import { isValidElement, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import useFetch from "./UseFetch";
import { useNavigate } from "react-router-dom";
import "./ListpageExpress.css";

const unwrapRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.smtp)) return payload.smtp;
  if (Array.isArray(payload.logs)) return payload.logs;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.value)) return payload.value;
  return [];
};

const sortValue = (value) => {
  if (Array.isArray(value)) return value.map(sortValue).join("");
  if (isValidElement(value)) return sortValue(value.props.children);
  if (typeof value === "boolean") return value ? 1 : 0;
  return value;
};

const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const toPath = (target) => {
  if (!target) return "/home";
  return target.startsWith("/") ? target : `/${target}`;
};

const StandardExpressList = ({
  apiUrl,
  token,
  columns,
  searchKeys = [],
  pageCaption,
  cardPageLink,
  actionvisibility = true,
  redirectTo,
  actions = [],
  createMode = "navigate",
  infiniteScroll = false,
  pageSize = 40,
}) => {
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [sort, setSort] = useState(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const navigate = useNavigate();

  const { data: fetchedData, loading, error } = useFetch(apiUrl, token);

  useEffect(() => {
    setData(unwrapRows(fetchedData));
  }, [fetchedData]);

  useEffect(() => {
    setVisibleCount(pageSize);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [filterText, data, pageSize, sort]);

  const handleCreateNew = () => {
    if (!cardPageLink) return;
    navigate(toPath(cardPageLink));
  };

  const searchKeysKey = searchKeys.join("|");
  const filteredData = useMemo(() => {
    const needle = filterText.toLowerCase();
    const keys = searchKeysKey.split("|");
    return data.filter((item) =>
      keys.some((key) =>
        String(item[key] ?? "")
          .toLowerCase()
          .includes(needle),
      ),
    );
  }, [data, filterText, searchKeysKey]);

  const sortedData = useMemo(() => {
    if (!infiniteScroll || !sort) return filteredData;
    const { column, direction } = sort;
    const factor = direction === "desc" ? -1 : 1;
    const compare = typeof column.sortFunction === "function"
      ? column.sortFunction
      : (a, b) =>
          compareValues(sortValue(column.selector(a)), sortValue(column.selector(b)));
    return [...filteredData].sort((a, b) => factor * compare(a, b));
  }, [filteredData, infiniteScroll, sort]);

  const displayedData = infiniteScroll
    ? sortedData.slice(0, visibleCount)
    : sortedData;
  const hasMore = infiniteScroll && visibleCount < sortedData.length;

  useEffect(() => {
    if (!infiniteScroll) return undefined;
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleCount((count) =>
          Math.min(count + pageSize, sortedData.length),
        );
      },
      { root, rootMargin: "160px", threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [infiniteScroll, sortedData.length, pageSize, displayedData.length]);

  if (loading) return <div className="express-list-status">Loading data...</div>;
  if (error)
    return (
      <div className="express-list-status" style={{ color: "red" }}>
        Error: {error}
      </div>
    );

  return (
    <div className="express-list-page">
      <div className="express-list-toolbar">
        <p>{pageCaption}</p>
        {redirectTo !== undefined && (
          <button
            type="button"
            className="express-list-btn"
            onClick={() => navigate(toPath(redirectTo))}
          >
            Back
          </button>
        )}
        {actionvisibility && createMode === "navigate" && cardPageLink && (
          <button
            type="button"
            className="express-list-btn"
            onClick={handleCreateNew}
          >
            Create New
          </button>
        )}
        {actions.map((action, i) => (
          <button
            key={i}
            type="button"
            className="express-list-btn"
            onClick={action.fx}
          >
            {action.icon && (
              <span style={{ marginRight: "6px" }}>{action.icon}</span>
            )}
            {action.name}
          </button>
        ))}
      </div>

      <div className="express-list-search">
        <input
          type="text"
          placeholder="Search..."
          className="search-input"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>

      <div
        ref={scrollRef}
        className={
          infiniteScroll
            ? "express-list-scroller express-list-scroller-infinite"
            : "express-list-scroller"
        }
      >
        <DataTable
          columns={columns}
          data={displayedData}
          pagination={!infiniteScroll}
          sortServer={infiniteScroll}
          onSort={(column, direction) => {
            if (!infiniteScroll) return;
            const usable =
              typeof column?.sortFunction === "function" ||
              typeof column?.selector === "function";
            setSort(usable ? { column, direction } : null);
          }}
          highlightOnHover
          striped
          responsive
          persistTableHead
          customStyles={{
            table: { style: { width: "100%" } },
            tableWrapper: { style: { width: "100%" } },
            headRow: {
              style: {
                position: infiniteScroll ? "sticky" : "static",
                top: 0,
                zIndex: 2,
                backgroundColor: "#ffffff",
              },
            },
          }}
        />
        {infiniteScroll && (
          <div ref={sentinelRef} className="express-list-sentinel">
            {hasMore
              ? "Loading more..."
              : filteredData.length
                ? "End of list"
                : "No records found"}
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardExpressList;
